import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CheckIcon from '@material-ui/icons/Check';
import DeleteIcon from '@material-ui/icons/Delete';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useReducer, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { openPage } from '@cedalo/webui/src/actions/actions';
import { Overlay } from '@cedalo/webui/src/components/HelperComponent/Overlay';
import gatewayClient from '@cedalo/webui/src/helper/GatewayClient';
import { useGraphQLCB } from '@cedalo/webui/src/helper/Hooks';
import { intl } from '@cedalo/webui/src/helper/IntlGlobalProvider';
import { AdminPageLayout } from '@cedalo/webui/src/layouts/AdminPageLayout';
import { filterAndSort } from '@cedalo/webui/src/pages/common';
import { Path } from '@cedalo/webui/src/helper/Path';

const WORKSPACE_TABLE_COLUMNS = [
	{ id: 'name', key: 'Extensions.Admin.Workspace.Column.name' },
	{ id: 'userCount', key: 'Extensions.Admin.Workspace.Column.userCount' },
	{ id: 'lastModified', key: 'Extensions.Admin.Workspace.Column.lastModified' }
];

// Workaround for Babel restriction, see https://github.com/yahoo/babel-plugin-react-intl/issues/119
function FormattedMessageFixed(props) {
	return <FormattedMessage {...props} />;
}

const workspaceShape = PropTypes.shape({
	name: PropTypes.string,
	userCount: PropTypes.number,
	lastModified: PropTypes.string
});

const WorkspaceTable = (props) => {
	const { workspaces, onDelete, onSelect, onSort, sortBy, sortDirection } = props;

	return (
		<Table size="small" style={{marginTop: '25px'}}>
			<TableHead>
				<TableRow>
					{WORKSPACE_TABLE_COLUMNS.map((column) => (
						<TableCell
							padding="none"
							key={column.id}
							sortDirection={sortBy === column.id ? sortDirection : false}
						>
							<TableSortLabel
								active={sortBy === column.id}
								direction={sortDirection}
								onClick={() => onSort(column.id)}
							>
								<FormattedMessageFixed id={column.key} defaultMessage={column.key} />
							</TableSortLabel>
						</TableCell>
					))}
					<TableCell />
				</TableRow>
			</TableHead>
			<TableBody>
				{workspaces.map((workspace) => (
					<TableRow
						hover
						key={workspace.id}
						onClick={() => onSelect(workspace.id)}
						style={{ cursor: 'pointer', height: '45px'}}
					>
						<TableCell padding="none">
							<b>{workspace.name}</b>
						</TableCell>
						<TableCell padding="none">{workspace.userCount}</TableCell>
						<TableCell padding="none">{moment(workspace.lastModified).fromNow()}</TableCell>
						<TableCell padding="none">
							{workspace.canDelete ? (
								<IconButton
									style={{ color: '#FF0022' }}
									onClick={(event) => {
										event.stopPropagation();
										onDelete(workspace.id);
									}}
								>
									<DeleteIcon />
								</IconButton>
							) : null}
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	);
};

WorkspaceTable.propTypes = {
	workspaces: PropTypes.arrayOf(workspaceShape).isRequired,
	sortBy: PropTypes.string,
	sortDirection: PropTypes.string,
	onDelete: PropTypes.func.isRequired,
	onSelect: PropTypes.func.isRequired,
	onSort: PropTypes.func.isRequired
};

WorkspaceTable.defaultProps = {
	sortBy: undefined,
	sortDirection: undefined
};

const QUERY = `
{
	workspaces {
		id
		name
		lastModified
		userCount
		canDelete
	}
}
`;

const DELETE_WORKSPACE_MUTATION = `
mutation DeleteWorkspace($id: ID!) {
	deleteWorkspace(id: $id) {
		success
		code
	}
}
`;

const defaultState = {
	workspaces: undefined,
	errors: null,
	loading: true,
	workspaceId: null,
	pending: false,
	reloading: false
};

const reducer = (state, action) => {
	switch (action.type) {
		case 'set_errors':
			return {
				...state,
				errors: action.data
			};
		case 'set_workspaces':
			return {
				...state,
				loading: false,
				workspaces: action.data
			};
		case 'set_workspace_to_delete':
			return {
				...state,
				workspaceToDelete: action.data,
				pending: false
			};
		case 'start_delete':
			return {
				...state,
				pending: true
			};
		case 'cancel_delete':
			return {
				...state,
				workspaceToDelete: null
			};
		case 'delete_success':
			return {
				...state,
				pending: false,
				reloading: true,
				workspaceToDelete: null
			};
		case 'done':
			return {
				...state,
				pending: false,
				reloading: false,
				workspaceToDelete: null
			};
		case 'reload_done':
			return {
				...state,
				reloading: false
			};
		default:
			throw new Error(`UserTablePage reducer received unknown action: ${action.type}`);
	}
};

const filterAndSortWorkspaces = filterAndSort(['name']);

const WorkspaceOverviewComponent = (props) => {
	const { onAddWorkspace, onSelectWorkspace } = props;
	const [filterText, setTextFilter] = useState('');
	const [sort, setSort] = useState({ field: 'name', direction: 'asc' });
	const [state, dispatch] = useReducer(reducer, defaultState);

	useGraphQLCB(
		({ data, errors }) => {
			if (data) {
				dispatch({ type: 'set_workspaces', data: data.workspaces });
			}
			if (errors) {
				dispatch({ type: 'set_errors', errors: data });
			}
		},
		QUERY,
		undefined,
		[state.reloading],
		() => !state.workspaces || state.reloading
	);

	useEffect(() => {
		if (state.reloading && state.workspaces) {
			setTimeout(() => dispatch({ type: 'reload_done' }), 500);
		}
	}, [state.workspaces]);

	if (state.errors) {
		return (
			<div
				style={{
					height: '100%',
					padding: '24px',
					backgroundColor: '#EEE',
					boxSizing: 'border-box',
					overflow: 'auto'
				}}
			>
				{state.errors.join('\n')}
			</div>
		);
	}

	const deleteWorkspace = async () => {
		dispatch({ type: 'start_delete' });
		try {
			const {
				deleteWorkspace: { success, code }
			} = await gatewayClient.graphql(DELETE_WORKSPACE_MUTATION, {
				id: state.workspaceToDelete
			});
			if (success) {
				dispatch({ type: 'delete_success' });
			} else {
				dispatch({ type: 'done' });
				console.error(`Failed to delete workspaces: ${code}`);
			}
		} catch (error) {
			dispatch({ type: 'done' });
			console.error(error);
		}
	};

	const workspaces = filterAndSortWorkspaces(state.workspaces, filterText, sort);

	return (
		<AdminPageLayout page="workspaces" documentTitle={intl.formatMessage({ id: 'Administration' })}>
			<div
				style={{
					maxHeight: '100%',
					padding: '24px',
					overflow: 'auto',
					boxSizing: 'border-box'
				}}
			>
				<Paper
					style={{
						padding: '32px',
						maxWidth: '960px',
						maxHeight: '100%',
						margin: 'auto',
						position: 'relative'
					}}
				>
					<Grid container spacing={2}>
						<Grid item container spacing={1} justify="space-between">
							<Grid item>
								<Typography variant="h5" gutterBottom>
									<FormattedMessage id="Extensions.Admin.Workspaces" defaultMessage="Workspaces" />
								</Typography>
							</Grid>
							<Grid item>
								<Button variant="contained" color="primary" onClick={onAddWorkspace}>
									<FormattedMessage
										id="Extensions.Admin.Workspace.add"
										defaultMessage="Add workspace"
									/>
								</Button>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							<TextField
								id="filter"
								label={
									<FormattedMessage id="Extensions.Admin.Workspace.search" defaultMessage="Search" />
								}
								fullWidth
								value={filterText}
								onChange={(event) => setTextFilter(event.target.value)}
							/>
						</Grid>
						<Grid item xs={12} style={{ overflowX: 'auto', width: '0px' }}>
							<WorkspaceTable
								workspaces={workspaces}
								onDelete={(workspaceId) =>
									dispatch({ type: 'set_workspace_to_delete', data: workspaceId })
								}
								onSelect={(workspaceId) => onSelectWorkspace(workspaceId)}
								onSort={(field) => {
									const direction = field === sort.field && sort.direction === 'asc' ? 'desc' : 'asc';
									setSort({ field, direction });
								}}
								sortBy={sort.field}
								sortDirection={sort.direction}
								loading={state.loading}
							/>
						</Grid>
						{!state.loading && workspaces.length === 0 && (
							<Grid item xs={12} style={{ textAlign: 'center' }}>
								<Typography variant="body2">No Workspaces Found</Typography>
							</Grid>
						)}
						{state.loading && (
							<Grid container style={{ minHeight: '200px' }} justify="center" alignItems="center">
								<Grid item>
									<CircularProgress />
								</Grid>
							</Grid>
						)}
					</Grid>
					{state.pending && (
						<Overlay>
							<CircularProgress style={{ width: '24px', height: '24px' }} />
						</Overlay>
					)}
					{state.reloading && (
						<Overlay>
							<CheckIcon color="primary" />
						</Overlay>
					)}
				</Paper>
			</div>
			<Dialog open={!!state.workspaceToDelete && !state.pending && !state.deleted}>
				<DialogTitle>
					<FormattedMessage id="Extensions.Admin.WorkspaceDelete" defaultMessage="Delete Workspace" />
				</DialogTitle>
				<DialogContent
					style={{
						marginTop: '20px'
					}}
				>
					<DialogContentText>
						<FormattedMessage
							id="Admin.deleteWorkspaceMessage"
							defaultMessage="Please confirm to delete this workspace."
						/>
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => dispatch({ type: 'cancel_delete' })} color="primary">
						<FormattedMessage id="Cancel" defaultMessage="Cancel" />
					</Button>
					<Button data-action="delete" onClick={() => deleteWorkspace()} color="primary" autoFocus>
						<FormattedMessage id="Delete" defaultMessage="Delete" />
					</Button>
				</DialogActions>
			</Dialog>
		</AdminPageLayout>
	);
};

WorkspaceOverviewComponent.propTypes = {
	onAddWorkspace: PropTypes.func.isRequired,
	onSelectWorkspace: PropTypes.func.isRequired
};

const mapDispatchToProps = {
	onAddWorkspace: () => openPage(Path.workspace('new')),
	onSelectWorkspace: (workspaceId) => openPage(Path.workspace(workspaceId))
};

export const WorkspaceOverviewPage = connect(null, mapDispatchToProps)(WorkspaceOverviewComponent);



// WEBPACK FOOTER //
// /home/travis/build/cedalo/streamsheets-pro-features/packages/webui-extensions/src/workspace/WorkspaceOverviewPage.js